import { create } from 'zustand'
import axios from 'axios'
import { cleanParamsFilter } from 'Utils/cleanParamsFilter'

import { CRUDBaseStore } from './Base/CRUD'

interface SurveyStore extends CRUDBaseStore {
  siteManagers: any[]
  saleUsers: any[]
  getSiteManagers: () => Promise<{ success: boolean; data: any }>
  getSaleUsers: () => Promise<{ success: boolean; data: any }>
  createProject: (id: number, data: any) => Promise<{ success: boolean; data: any }>
  getOne: (id: number) => Promise<{ success: boolean; data: any }>
  getImage: (id: number, fileName: string) => Promise<{ success: boolean; data: any }>
  getImagePath: (id: number, fileName: string) => string
  uploadImage: (id: number, imgFile: any) => Promise<{ success: boolean; data: any }>
  downloadPdf: (id: number) => Promise<{ success: boolean; data: any }>
  updateImageDescription: (
    id: number,
    fileId: number,
    description: string
  ) => Promise<{ success: boolean; data: any }>
  deleteImage: (id: number, fileId: number) => Promise<{ success: boolean; data: any }>
  approve: (id: number) => Promise<{ success: boolean; data: any }>
  reject: (id: number, data: any) => Promise<{ success: boolean; data: any }>
  downloadApproverSignature: (id: number) => Promise<{ success: boolean; data: any }>
  uploadApproverSignature: (id: number, data: any) => Promise<{ success: boolean; data: any }>
  newVersion: (id: number) => Promise<{ success: boolean; data: any }>
  deleteAll: (data: any) => Promise<{ success: boolean; data: any }>
}

export const API_SLUG = 'survey'

export interface BuildingInArea {
  [direction: string]: {
    characteristics: string
    quantity: string
    condition: string
    distance: string
  }
}

// export interface FactoryItem {
//   distance: string
//   factory: string
export interface FactoryItem {
  distance: string
  factory: string
}

export interface InitialState {
  // Status
  documentStatus: string
  jobStatus: string
  // Head
  saleOrderId: number | null
  saleOrder: any
  customerTitle: string
  customerEmail: string
  saleTitle: string
  division: string
  address: string
  contactPersonFullName: string
  contactPersonPhone: string
  leadEngineerFullName: string
  leadEngineerPhone: string
  onSiteAvailableTime: string
  pilingAvailableTime: string
  // Step 1
  shippingOnly: boolean
  installationCategory: any[]
  installationCategoryOption: string
  installationPlanner: string
  haveGroundworkPlan: string
  haveGroundworkPlanOther: string
  haveLayoutPlan: string
  haveLayoutPlanOther: string
  useDavit: boolean
  davitSize: string
  davitHeightMeter: string
  davitQuantity: string
  usePD: boolean
  pdSize: string
  pdHeightMeter: string
  pdQuantity: string
  useLIP: boolean
  lipSize: string
  lipHeightMeter: string
  lipQuantity: string
  davitHeadSystem: string
  davitHeadSize: string
  installationKits: string
  installationKitSize: string
  installationKitsLength: string
  maxHeightEntranceMeter: string
  transportationByVehicle: string
  transportationAvailableTime: string
  // distanceFromFactoryKm: FactoryItem
  // distanceFromFactoryKm2: FactoryItem
  // distanceFromFactoryKm3: FactoryItem
  distanceFromFactoryKm: string
  distanceFromFactoryKm2: string
  distanceFromFactoryKm3: string
  craneToPole: string
  craneToPoleOther: string
  haveBuildingInArea: string
  buildingInAreaEffectCondition: string
  buildingInArea: BuildingInArea
  adjacentPictureNorth: string | null
  adjacentPictureSouth: string | null
  adjacentPictureEast: string | null
  adjacentPictureWest: string | null
  buildingInAreaResponsibility: string
  riskOfDrilling: string
  riskOfDrillingPlan: string
  latitude: number
  longitude: number
  // Step 2
  obstacleOnGround: string
  obstacleOnGroundRemark: string
  obstacleUnderGround: string
  obstacleUnderGroundRemark: string
  truckEntrance: string
  areaRai: string
  areaNgan: string
  areaSquareWa: string
  areaWidth: string
  areaLength: string
  areaFence: string
  entranceWidth: string
  entranceHeight: string
  entranceLength: string
  levelOfArea: string
  levelOfAreaHigherLevel: string
  levelOfAreaLowerLevel: string
  areaCondition: string
  areaConditionAddition: string
  roadEntrance: string
  roadEntranceWidth: string
  haveRoadInside: string
  haveRoadInsideRemark1: string
  haveRoadInsideRemark2: string
  haveRoadInsideRemark3: string
  haveProblem1: string
  haveProblem2: string
  haveProblem3: string
  polePointSurface: string
  polePointSurfaceAmount: string
  polePointSurfaceAmount2: string
  polePointSurfaceAmount3: string
  areaAdjustment: string
  electricityPreparation: string
  electricityHouse: string
  electricityLight: string
  electricityFCPTG: string
  houseForEmployee: string
  employeeTransportation: string
  employeeTransportationNote: string
  noteOther: string
  // Step 3
  noteRiskFinancial: string
  safetyRequest: string
  surveyUploadImages: any[]
  surveySaleOrderItems: any[]
  // Date
  updatedAt: string
  sendQuotationDate: string
  contractAgreedDate: string
  rigToSiteDate: string
  projectStartDate: string
  contractStartDate: string
  lastPiledDate: string
  surveySaleUsers: any[]
}

export const INITIAL_STATE: InitialState = {
  // Status
  documentStatus: 'draft',
  jobStatus: 'planning',
  // Head
  saleOrderId: null,
  saleOrder: {},
  customerEmail: '',
  customerTitle: '',
  saleTitle: '',
  division: '',
  address: '',
  contactPersonFullName: '',
  contactPersonPhone: '',
  leadEngineerFullName: '',
  leadEngineerPhone: '',
  onSiteAvailableTime: '',
  pilingAvailableTime: '',
  // Step 1
  shippingOnly: false,
  installationCategory: [],
  installationCategoryOption: '',
  installationPlanner: '',
  haveGroundworkPlan: '',
  haveGroundworkPlanOther: '',
  haveLayoutPlan: '',
  haveLayoutPlanOther: '',
  useDavit: false,
  davitSize: '',
  davitHeightMeter: '',
  davitQuantity: '',
  usePD: false,
  pdSize: '',
  pdHeightMeter: '',
  pdQuantity: '',
  useLIP: false,
  lipSize: '',
  lipHeightMeter: '',
  lipQuantity: '',
  davitHeadSystem: '',
  davitHeadSize: '',
  installationKits: '',
  installationKitSize: '',
  installationKitsLength: '',
  maxHeightEntranceMeter: '',
  transportationByVehicle: '',
  transportationAvailableTime: '',
  distanceFromFactoryKm: '',
  distanceFromFactoryKm2: '',
  distanceFromFactoryKm3: '',
  // distanceFromFactoryKm: {
  //   distance: '',
  //   factory: '',
  // },
  // distanceFromFactoryKm2: {
  //   distance: '',
  //   factory: '',
  // },
  // distanceFromFactoryKm3: {
  //   distance: '',
  //   factory: '',
  // },
  craneToPole: '',
  craneToPoleOther: '',
  haveBuildingInArea: '',
  buildingInAreaEffectCondition: '',
  buildingInArea: {
    north: {
      characteristics: '',
      quantity: '',
      condition: '',
      distance: '',
    },
    south: {
      characteristics: '',
      quantity: '',
      condition: '',
      distance: '',
    },
    west: {
      characteristics: '',
      quantity: '',
      condition: '',
      distance: '',
    },
    east: {
      characteristics: '',
      quantity: '',
      condition: '',
      distance: '',
    },
  },
  adjacentPictureNorth: null,
  adjacentPictureSouth: null,
  adjacentPictureEast: null,
  adjacentPictureWest: null,
  buildingInAreaResponsibility: '',
  riskOfDrilling: '',
  riskOfDrillingPlan: '',
  latitude: undefined as any,
  longitude: undefined as any,
  // Step 2
  obstacleOnGround: '',
  obstacleOnGroundRemark: '',
  obstacleUnderGround: '',
  obstacleUnderGroundRemark: '',
  truckEntrance: '',
  areaRai: '',
  areaNgan: '',
  areaSquareWa: '',
  areaWidth: '',
  areaLength: '',
  areaFence: '',
  entranceWidth: '',
  entranceHeight: '',
  entranceLength: '',
  levelOfArea: '',
  levelOfAreaHigherLevel: '',
  levelOfAreaLowerLevel: '',
  areaCondition: '',
  areaConditionAddition: '',
  roadEntrance: '',
  roadEntranceWidth: '',
  haveRoadInside: '',
  haveRoadInsideRemark1: '',
  haveRoadInsideRemark2: '',
  haveRoadInsideRemark3: '',
  haveProblem1: '',
  haveProblem2: '',
  haveProblem3: '',
  polePointSurface: '',
  polePointSurfaceAmount: '',
  polePointSurfaceAmount2: '',
  polePointSurfaceAmount3: '',
  areaAdjustment: '',
  electricityPreparation: '',
  electricityHouse: '',
  electricityLight: '',
  electricityFCPTG: '',
  houseForEmployee: '',
  employeeTransportation: '',
  employeeTransportationNote: '',
  noteOther: '',
  // Step 3
  noteRiskFinancial: '',
  safetyRequest: '',
  surveyUploadImages: [],
  surveySaleOrderItems: [],
  // Date
  updatedAt: '',
  sendQuotationDate: '',
  contractAgreedDate: '',
  rigToSiteDate: '',
  projectStartDate: '',
  contractStartDate: '',
  lastPiledDate: '',
  surveySaleUsers: [],
}

export const useSurveyStore = create<SurveyStore>((set, get) => ({
  siteManagers: [],
  saleUsers: [],
  data: [],
  selected: INITIAL_STATE,
  loading: false,
  error: undefined,
  setLoading: (loading: boolean) => {
    set({ loading })
  },
  setError: (error: any) => {
    set({ error })
  },
  setSelected: (selected: any) => {
    set({ selected })
  },
  getSiteManagers: async () => {
    set({ loading: true })
    return axios
      .get(`/${API_SLUG}/site-managers`)
      .then(res => {
        set({ siteManagers: res.data })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getSaleUsers: async () => {
    set({ loading: true })
    return axios
      .get(`/${API_SLUG}/sales`)
      .then(res => {
        set({ saleUsers: res.data })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getAll: async (filter: any) => {
    set({ loading: true })
    const finaleFilter = cleanParamsFilter(filter)

    return axios
      .get(`/${API_SLUG}`, { params: finaleFilter })
      .then(res => {
        set({ data: res.data })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getOne: async id => {
    set({ loading: true })

    return axios
      .get(`/${API_SLUG}/${id}`)
      .then(res => {
        set({ selected: res.data })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  create: async (data: any) => {
    set({ loading: true })

    const formData = new FormData()
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined) {
        if (key === 'saleOrderItemsBody') {
          Object.keys(data[key]).forEach(itemKey => {
            formData.append(`${itemKey}`, data[key][itemKey])
          })
        } else if (key === 'surveySaleUsers') {
          data[key].forEach((su: any, index: number) => {
            formData.append(`surveySaleUsers[${index}][userId]`, su.userId)
          })
        } else {
          formData.append(key, data[key])
        }
      }
    })

    return axios
      .post(`/${API_SLUG}`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  edit: async (id, data) => {
    set({ loading: true })

    const formData = new FormData()
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined) {
        if (key === 'saleOrderItemsBody') {
          Object.keys(data[key]).forEach(itemKey => {
            formData.append(`${itemKey}`, data[key][itemKey])
          })
        } else if (key === 'surveySaleUsers') {
          data[key].forEach((su: any, index: number) => {
            formData.append(`surveySaleUsers[${index}][userId]`, su.userId)
          })
        } else {
          formData.append(key, data[key])
        }
      }
    })

    return axios
      .patch(`/${API_SLUG}/${id}`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },

  delete: async id => {
    set({ loading: true })
    return axios
      .delete(`/${API_SLUG}/${id}`)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  createProject: async (id, data) => {
    set({ loading: true })
    return axios
      .post(`/${API_SLUG}/${id}/create-project`, data)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        console.log(err)
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })

        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getImage: async (id, fileName) => {
    set({ loading: true })
    return axios
      .get(`/${API_SLUG}/${id}/image/${fileName}`)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getImagePath: (id, fileName) => {
    return `/${API_SLUG}/${id}/image/${fileName}`
  },

  downloadPdf: async id => {
    set({ loading: true })
    return axios
      .get(`/${API_SLUG}/${id}/downloadPdf`, { responseType: 'blob' })
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },

  uploadImage: async (id, imgFiles) => {
    set({ loading: true })

    console.log(imgFiles)

    const formData = new FormData()
    for (let imgFile of imgFiles) {
      formData.append('file', imgFile)
    }

    return axios
      .post(`/${API_SLUG}/${id}/uploadImage`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  updateImageDescription: async (id, fileId, description) => {
    set({ loading: true })

    const formData = new FormData()
    formData.append('description', description)

    return axios
      .patch(`/${API_SLUG}/${id}/image/${fileId}`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  deleteImage: async (id, fileId) => {
    set({ loading: true })
    return axios
      .delete(`/${API_SLUG}/${id}/image/${fileId}`)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  approve: async id => {
    set({ loading: true })
    return axios
      .post(`/${API_SLUG}/${id}/approve`)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  reject: async (id, data) => {
    set({ loading: true })
    return axios
      .post(`/${API_SLUG}/${id}/reject`, data)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  downloadApproverSignature: async id => {
    set({ loading: true })
    return axios
      .get(`/${API_SLUG}/${id}/approver-signature`, { responseType: 'blob' })
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  uploadApproverSignature: async (id, data) => {
    set({ loading: true })

    return axios
      .post(`/${API_SLUG}/${id}/upload-approver-signature`, data)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },

  newVersion: async (id: number) => {
    set({ loading: true })

    return axios
      .post(`/${API_SLUG}/${id}/new-version`)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },

  deleteAll: async (data: any) => {
    set({ loading: true })
    return axios
      .post(`/${API_SLUG}/delete-multiple`, data)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },

  clearState: () => {
    set({
      data: [],
      loading: false,
      error: undefined,
      selected: INITIAL_STATE,
    })
  },
}))
